import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as Logo } from '../../svg/newlogo.svg';
import styles from '../../styles/centumseatlistmobile.module.css';
import { useCentumBriefingStats } from '../../lib/useCentumBriefingStats';

const SEATMAP_URL =
  'https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/remind/5_%EC%A2%8C%EC%84%9D%ED%91%9C_%EC%88%98%EB%8A%A5%EC%84%A0%EB%B0%B0%20_%EC%84%BC%ED%85%80%EC%A0%90_%EC%B5%9C%EC%A2%85.webp';

const SLIDES = [
  {
    src: 'https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/remind/KakaoTalk_20260527_212351281.webp',
    alt: '센텀점 데스크',
    cap: '센텀점 데스크',
  },
  {
    src: 'https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/remind/KakaoTalk_20260527_212351281_01.webp',
    alt: '센텀점 자습실',
    cap: '센텀점 휴게실 플랜테리어',
  },
  {
    src: 'https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/remind/KakaoTalk_20260527_212351281_02.webp',
    alt: '센텀점 라운지',
    cap: '센텀점 휴게실',
  },
  {
    src: 'https://pub-81fe206b05a84c2da16285ce68449232.r2.dev/sunbae/remind/KakaoTalk_20260527_212351281_03.webp',
    alt: '센텀점 입구',
    cap: '센텀점 자습실',
  },
];

// 전화 / 카카오 — 사이트 기존 값 그대로 (speeddial.tsx, briefingBranchConfig.ts 와 동일)
const TEL_HREF = 'tel://16685786';
const KAKAO_URL = 'http://pf.kakao.com/_Whxexgxj/chat';

// 헤더 연락 아이콘 (헤드셋) — nav-mobile.js CONTACT_ICON
const ContactIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
    <path d="M21 16v2a4 4 0 0 1-4 4h-5" />
  </svg>
);
const PhoneIconS = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 4.5h3l1.5 4-2 1.5a11 11 0 0 0 6 6l1.5-2 4 1.5v3a1.5 1.5 0 0 1-1.6 1.5C9.5 19.5 4.5 14.5 4 6.1A1.5 1.5 0 0 1 5.5 4.5z" />
  </svg>
);
const TalkIconS = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4c5 0 9 3.2 9 7.2 0 4-4 7.2-9 7.2-.9 0-1.7-.1-2.5-.3L5 19.5l1.2-3.1A6.7 6.7 0 0 1 3 11.2C3 7.2 7 4 12 4z" />
  </svg>
);

// 숫자 0 → target 카운트업 (원본 LIVE 스크립트와 동일한 cubic-ease-out, 1.4s)
function useCountUp(target: number, duration: number, start: boolean) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) { setVal(0); return; }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

export default function CentumSeatlistMobile() {
  const stats = useCentumBriefingStats('centum');
  const hasTotal = typeof stats.total === 'number';
  const count = useCountUp(stats.total ?? 0, 1400, hasTotal && !stats.loading);

  const [contactOpen, setContactOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const headerActionsRef = useRef<HTMLDivElement>(null);

  // 헤더 자동 숨김 — 아래로 스크롤하면 숨고, 위로 올리면 다시 표시 (Toss 스타일)
  useEffect(() => {
    const THRESHOLD = 8;   // 미세 흔들림 무시
    const TOP_GUARD = 32;  // 맨 위 근처에서는 항상 표시
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - lastY) < THRESHOLD) { ticking = false; return; }
      if (y < TOP_GUARD) {
        setHeaderHidden(false);
      } else if (y > lastY) {
        setHeaderHidden(true);   // 아래로 → 숨김
        setContactOpen(false);   // 숨길 때 열린 팝오버 닫기
      } else {
        setHeaderHidden(false);  // 위로 → 표시
      }
      lastY = y;
      ticking = false;
    };
    const handler = () => {
      if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // 팝오버 바깥 클릭 시 닫기
  useEffect(() => {
    if (!contactOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (headerActionsRef.current && !headerActionsRef.current.contains(e.target as Node)) {
        setContactOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [contactOpen]);

  // ── PRE 캐러셀 — 자동 넘김 + 진행 바 + 드래그/스와이프 (원본 스크립트 이식) ──
  const vpRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vp = vpRef.current;
    const track = trackRef.current;
    const dotsWrap = dotsRef.current;
    if (!vp || !track || !dotsWrap) return;
    const slides = Array.from(track.children) as HTMLElement[];
    const dots = Array.from(dotsWrap.children) as HTMLElement[];
    if (!slides.length || !dots.length) return;

    const SLIDE_MS = 4000;
    let current = 0;
    let timer: any = null;
    let paused = false;

    const fillOf = (d: HTMLElement) => d.firstElementChild as HTMLElement | null;

    function setActive(i: number, animate: boolean) {
      current = i;
      dots.forEach((d, k) => {
        const fill = fillOf(d);
        d.classList.toggle(styles.mnPreDotActive, k === i);
        d.classList.toggle(styles.mnPreDotDone, k < i);
        if (!fill) return;
        if (k === i) {
          fill.style.transition = 'none';
          fill.style.width = '0%';
          void fill.offsetWidth;
          if (animate && !paused) {
            fill.style.transition = 'width ' + SLIDE_MS + 'ms linear';
            fill.style.width = '100%';
          }
        } else if (k < i) {
          fill.style.transition = 'none';
          fill.style.width = '100%';
        } else {
          fill.style.transition = 'none';
          fill.style.width = '0%';
        }
      });
    }

    function goTo(i: number) {
      const idx = ((i % slides.length) + slides.length) % slides.length;
      const s = slides[idx];
      vp!.scrollTo({ left: s.offsetLeft - vp!.offsetLeft, behavior: 'smooth' });
      setActive(idx, true);
      restart();
    }
    function next() { goTo(current + 1); }
    function restart() {
      if (timer) clearTimeout(timer);
      if (paused) return;
      timer = setTimeout(next, SLIDE_MS);
    }

    function pauseFill() {
      const fill = fillOf(dots[current]);
      if (!fill) return;
      const w = fill.getBoundingClientRect().width;
      const parentW = (fill.parentElement as HTMLElement).getBoundingClientRect().width || 1;
      fill.style.transition = 'none';
      fill.style.width = (w / parentW * 100) + '%';
    }
    function resumeFill() {
      const fill = fillOf(dots[current]);
      if (!fill) return;
      const w = fill.getBoundingClientRect().width;
      const parentW = (fill.parentElement as HTMLElement).getBoundingClientRect().width || 1;
      const remainingRatio = 1 - (w / parentW);
      const remainingMs = Math.max(200, SLIDE_MS * remainingRatio);
      void fill.offsetWidth;
      fill.style.transition = 'width ' + remainingMs + 'ms linear';
      fill.style.width = '100%';
      if (timer) clearTimeout(timer);
      timer = setTimeout(next, remainingMs);
    }

    // 스크롤 동기화
    let scrollDebounce: any = null;
    const onScroll = () => {
      if (timer) clearTimeout(timer);
      if (scrollDebounce) clearTimeout(scrollDebounce);
      scrollDebounce = setTimeout(() => {
        const left = vp!.scrollLeft;
        let idx = 0, best = Infinity;
        slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - vp!.offsetLeft - left);
          if (d < best) { best = d; idx = i; }
        });
        setActive(idx, true);
        restart();
      }, 120);
    };
    vp.addEventListener('scroll', onScroll, { passive: true });

    const onEnter = () => { paused = true; if (timer) clearTimeout(timer); pauseFill(); };
    const onLeave = () => { paused = false; resumeFill(); restart(); };
    vp.addEventListener('pointerenter', onEnter);
    vp.addEventListener('pointerleave', onLeave);

    const dotClicks = dots.map((d, i) => {
      const h = () => goTo(i);
      d.addEventListener('click', h);
      return h;
    });

    // 포인터 드래그 스와이프
    let dragStartX = 0, dragStartScroll = 0, dragging = false, dragMoved = 0;
    const onDown = (e: PointerEvent) => {
      dragging = true; dragMoved = 0;
      dragStartX = e.clientX; dragStartScroll = vp!.scrollLeft;
      paused = true;
      if (timer) clearTimeout(timer);
      pauseFill();
      try { vp!.setPointerCapture(e.pointerId); } catch (_) {}
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - dragStartX;
      dragMoved = Math.abs(dx);
      vp!.scrollLeft = dragStartScroll - dx;
    };
    const endDrag = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      try { vp!.releasePointerCapture(e.pointerId); } catch (_) {}
      const left = vp!.scrollLeft;
      let idx = 0, best = Infinity;
      slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft - vp!.offsetLeft - left);
        if (d < best) { best = d; idx = i; }
      });
      const s = slides[idx];
      vp!.scrollTo({ left: s.offsetLeft - vp!.offsetLeft, behavior: 'smooth' });
      setActive(idx, true);
      paused = false;
      restart();
    };
    vp.addEventListener('pointerdown', onDown);
    vp.addEventListener('pointermove', onMove);
    vp.addEventListener('pointerup', endDrag);
    vp.addEventListener('pointercancel', endDrag);
    const onPLeave = (e: PointerEvent) => { if (dragging) endDrag(e); };
    vp.addEventListener('pointerleave', onPLeave);
    const onClickCapture = (e: MouseEvent) => {
      if (dragMoved > 6) { e.preventDefault(); e.stopPropagation(); }
    };
    vp.addEventListener('click', onClickCapture, true);

    // 화면 밖이면 정지, 보이면 재개
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { if (!paused) restart(); }
        else { if (timer) clearTimeout(timer); }
      });
    }, { threshold: 0.25 });
    io.observe(vp);

    setActive(0, true);
    restart();

    return () => {
      if (timer) clearTimeout(timer);
      if (scrollDebounce) clearTimeout(scrollDebounce);
      io.disconnect();
      vp.removeEventListener('scroll', onScroll);
      vp.removeEventListener('pointerenter', onEnter);
      vp.removeEventListener('pointerleave', onLeave);
      vp.removeEventListener('pointerdown', onDown);
      vp.removeEventListener('pointermove', onMove);
      vp.removeEventListener('pointerup', endDrag);
      vp.removeEventListener('pointercancel', endDrag);
      vp.removeEventListener('pointerleave', onPLeave);
      vp.removeEventListener('click', onClickCapture, true);
      dots.forEach((d, i) => d.removeEventListener('click', dotClicks[i]));
    };
  }, []);

  // ── 좌석표 라이트박스 — 줌/패닝 (원본 스크립트 이식) ──
  const lbRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const lbImgRef = useRef<HTMLImageElement>(null);
  const pctRef = useRef<HTMLButtonElement>(null);
  const seatmapRef = useRef<HTMLButtonElement>(null);
  const inRef = useRef<HTMLButtonElement>(null);
  const outRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const lb = lbRef.current;
    const stage = stageRef.current;
    const img = lbImgRef.current;
    const pct = pctRef.current;
    const seatmapBtn = seatmapRef.current;
    const closeBtn = closeRef.current;
    const inBtn = inRef.current;
    const outBtn = outRef.current;
    if (!lb || !stage || !img || !pct) return;

    const MIN = 1, MAX = 5;
    let scale = 1, tx = 0, ty = 0;
    let baseW = 0, baseH = 0;

    function measureBase() {
      img!.style.maxWidth = '92vw';
      img!.style.maxHeight = '88vh';
      img!.style.width = 'auto';
      img!.style.height = 'auto';
      img!.style.transform = 'none';
      baseW = img!.clientWidth;
      baseH = img!.clientHeight;
      img!.style.maxWidth = 'none';
      img!.style.maxHeight = 'none';
    }
    function apply() {
      if (baseW) {
        img!.style.width = (baseW * scale) + 'px';
        img!.style.height = (baseH * scale) + 'px';
      }
      img!.style.transform = `translate(${tx}px, ${ty}px)`;
      pct!.textContent = Math.round(scale * 100) + '%';
      stage!.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
    }
    function clampPan() {
      const r = img!.getBoundingClientRect();
      const maxX = Math.max(0, (r.width - stage!.clientWidth) / 2 + 40);
      const maxY = Math.max(0, (r.height - stage!.clientHeight) / 2 + 40);
      tx = Math.min(maxX, Math.max(-maxX, tx));
      ty = Math.min(maxY, Math.max(-maxY, ty));
    }
    function setScale(nextRaw: number, cx: number, cy: number) {
      const nextV = Math.min(MAX, Math.max(MIN, nextRaw));
      if (nextV === scale) return;
      const rect = stage!.getBoundingClientRect();
      const ox = (cx - rect.left - rect.width / 2 - tx);
      const oy = (cy - rect.top - rect.height / 2 - ty);
      const k = nextV / scale;
      tx -= ox * (k - 1);
      ty -= oy * (k - 1);
      scale = nextV;
      if (scale === 1) { tx = 0; ty = 0; }
      clampPan();
      apply();
    }
    function reset() { scale = 1; tx = 0; ty = 0; apply(); }
    function open() {
      lb!.classList.add(styles.mnLbOpen);
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => { measureBase(); reset(); });
    }
    function close() {
      lb!.classList.remove(styles.mnLbOpen);
      document.body.style.overflow = '';
    }

    const onSeatmap = () => open();
    seatmapBtn?.addEventListener('click', onSeatmap);
    const onClose = () => close();
    closeBtn?.addEventListener('click', onClose);
    const onIn = () => { const r = stage!.getBoundingClientRect(); setScale(scale + 0.5, r.left + r.width / 2, r.top + r.height / 2); };
    const onOut = () => { const r = stage!.getBoundingClientRect(); setScale(scale - 0.5, r.left + r.width / 2, r.top + r.height / 2); };
    inBtn?.addEventListener('click', onIn);
    outBtn?.addEventListener('click', onOut);
    const onPct = () => reset();
    pct.addEventListener('click', onPct);

    const onStageClick = (e: MouseEvent) => { if (e.target === stage && scale === 1) close(); };
    stage.addEventListener('click', onStageClick);
    const onDbl = (e: MouseEvent) => { e.preventDefault(); setScale(scale > 1 ? 1 : 2.4, e.clientX, e.clientY); };
    img.addEventListener('dblclick', onDbl);
    const onKey = (e: KeyboardEvent) => {
      if (!lb!.classList.contains(styles.mnLbOpen)) return;
      if (e.key === 'Escape') close();
      else if (e.key === '+' || e.key === '=') { const r = stage!.getBoundingClientRect(); setScale(scale + 0.5, r.left + r.width / 2, r.top + r.height / 2); }
      else if (e.key === '-') { const r = stage!.getBoundingClientRect(); setScale(scale - 0.5, r.left + r.width / 2, r.top + r.height / 2); }
    };
    document.addEventListener('keydown', onKey);
    const onWheel = (e: WheelEvent) => { e.preventDefault(); setScale(scale * (e.deltaY < 0 ? 1.12 : 0.89), e.clientX, e.clientY); };
    stage.addEventListener('wheel', onWheel, { passive: false });

    // 마우스 드래그 패닝
    let dragging = false, sx = 0, sy = 0;
    const onMouseDown = (e: MouseEvent) => {
      if (scale <= 1) return;
      dragging = true; sx = e.clientX - tx; sy = e.clientY - ty;
      stage!.classList.add(styles.mnLbStagePanning);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      tx = e.clientX - sx; ty = e.clientY - sy; clampPan(); apply();
    };
    const onMouseUp = () => { dragging = false; stage!.classList.remove(styles.mnLbStagePanning); };
    stage.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // 터치: 드래그(1) + 핀치(2)
    let pTx = 0, pTy = 0, pDist = 0, pScale = 1, pCx = 0, pCy = 0, lastTap = 0;
    const distOf = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        pTx = t.clientX - tx; pTy = t.clientY - ty;
        const now = Date.now();
        if (now - lastTap < 300) { setScale(scale > 1 ? 1 : 2.4, t.clientX, t.clientY); }
        lastTap = now;
      } else if (e.touches.length === 2) {
        pDist = distOf(e.touches); pScale = scale;
        pCx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        pCy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1 && scale > 1) {
        const t = e.touches[0];
        tx = t.clientX - pTx; ty = t.clientY - pTy; clampPan(); apply();
      } else if (e.touches.length === 2) {
        const d = distOf(e.touches);
        if (pDist > 0) setScale(pScale * (d / pDist), pCx, pCy);
      }
    };
    stage.addEventListener('touchstart', onTouchStart, { passive: false });
    stage.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      seatmapBtn?.removeEventListener('click', onSeatmap);
      closeBtn?.removeEventListener('click', onClose);
      inBtn?.removeEventListener('click', onIn);
      outBtn?.removeEventListener('click', onOut);
      pct.removeEventListener('click', onPct);
      stage.removeEventListener('click', onStageClick);
      img.removeEventListener('dblclick', onDbl);
      document.removeEventListener('keydown', onKey);
      stage.removeEventListener('wheel', onWheel);
      stage.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      stage.removeEventListener('touchstart', onTouchStart);
      stage.removeEventListener('touchmove', onTouchMove);
      document.body.style.overflow = '';
    };
  }, []);

  const subText =
    typeof stats.last24h === 'number' && stats.last24h > 0
      ? <>오늘만 <b>+{stats.last24h}명</b>이 신청했어요.</>
      : <>실시간으로 신청이 이어지고 있어요.</>;

  return (
    <div className={styles.page}>
      <main className={styles.frame}>

        {/* ── 상단 고정 헤더 ── */}
        <div className={`${styles.topbar} ${headerHidden ? styles.topbarHidden : ''}`}>
          <header className={styles.header}>
            <a className={styles.headerLogo} href="/" aria-label="수능선배 홈으로">
              <Logo />
            </a>
            <div className={styles.headerActions} ref={headerActionsRef}>
              <button
                className={styles.headerIcon}
                type="button"
                aria-label="상담 매체 선택"
                aria-haspopup="true"
                aria-expanded={contactOpen}
                onClick={(e) => { e.stopPropagation(); setContactOpen(o => !o); }}
              >
                <ContactIcon />
              </button>
              <div className={`${styles.contactPop} ${contactOpen ? styles.contactPopOpen : ''}`} role="menu">
                <a href={TEL_HREF} role="menuitem" onClick={() => setContactOpen(false)}>
                  <PhoneIconS /><span>전화로 연결</span>
                </a>
                <a href={KAKAO_URL} role="menuitem" target="_blank" rel="noopener noreferrer" onClick={() => setContactOpen(false)}>
                  <TalkIconS /><span>카카오톡 상담</span>
                </a>
              </div>
            </div>
          </header>
        </div>

        {/* ── PRE · 센텀점 선공개 (carousel) ── */}
        <section className={styles.mnPre}>
          <h2 className={styles.mnPreH}>{'수능선배\n센텀점 선공개'}</h2>
          <div className={styles.mnPreViewport} ref={vpRef}>
            <div className={styles.mnPreTrack} ref={trackRef}>
              {SLIDES.map((s, i) => (
                <figure className={styles.mnPreSlide} key={i}>
                  <div className={styles.mnPreMedia}>
                    <img src={s.src} alt={s.alt} loading={i === 0 ? undefined : 'lazy'} />
                  </div>
                  <figcaption className={styles.mnPreCap}>{s.cap}</figcaption>
                </figure>
              ))}
            </div>
          </div>
          <div className={styles.mnPreDots} ref={dotsRef} role="tablist" aria-label="센텀점 미리보기 슬라이드">
            {SLIDES.map((_, i) => (
              <button
                className={`${styles.mnPreDot} ${i === 0 ? styles.mnPreDotActive : ''}`}
                type="button"
                key={i}
                aria-label={`슬라이드 ${i + 1}`}
              >
                <span className={styles.mnPreDotFill} />
              </button>
            ))}
          </div>
        </section>

        {/* ── LIVE 신청자 배너 ── */}
        <section className={styles.lcSection}>
          <div className={styles.lcLive}>
            <div className={styles.lcLiveTop}>
              <span className={styles.lcLivePulse} />
              LIVE · 누적 설명회 신청자
            </div>
            <div className={styles.lcLiveCount}>
              <span className={styles.lcLiveNum}>{count.toLocaleString()}</span>
              <span className={styles.lcLiveUnit}>명 신청</span>
            </div>
            <p className={styles.lcLiveSub}>{subText}</p>
          </div>
        </section>

        {/* ── 센텀점 좌석 안내 ── */}
        <section className={styles.mnSystems}>
          <div className={styles.mnSystemsHead}>
            <h2 className={styles.mnSystemsH}>{'수능선배\n센텀점 좌석 안내'}</h2>
          </div>
          <hr className={styles.mnSystemsRule} />
          <button className={styles.mnSeatmap} type="button" ref={seatmapRef} aria-label="센텀점 좌석표 확대해서 보기">
            <img src={SEATMAP_URL} alt="수능선배 센텀점 좌석표" loading="lazy" />
            <span className={styles.mnSeatmapHint}>
              <span className={styles.mnSeatmapIco}>⤢</span> 좌석표 확대
            </span>
          </button>
        </section>
      </main>

      {/* ── 좌석표 라이트박스 ── */}
      <div className={styles.mnLb} ref={lbRef} role="dialog" aria-modal="true" aria-label="센텀점 좌석표">
        <button className={styles.mnLbClose} type="button" ref={closeRef} aria-label="닫기">×</button>
        <div className={styles.mnLbStage} ref={stageRef}>
          <img className={styles.mnLbImg} ref={lbImgRef} src={SEATMAP_URL} alt="수능선배 센텀점 좌석표" />
        </div>
        <div className={styles.mnLbZoom}>
          <button type="button" ref={outRef} aria-label="축소">−</button>
          <button type="button" className={styles.mnLbPct} ref={pctRef} aria-label="원래 크기">100%</button>
          <button type="button" ref={inRef} aria-label="확대">+</button>
        </div>
      </div>
    </div>
  );
}
